<template>
  <ValidationObserver ref="observer" data-testid="household-member-step">
    <OnboardingModalContent
      v-if="!success"
      :title="$t('onboarding_household_subtitle')"
      :error="formError"
    >
      <div class="tw-grid xl:tw-grid-cols-2 xl:tw-grid-rows-2 xl:tw-gap-x-7">
        <OnboardingField
          for-id="member-first-name"
          :label="$t('onboarding_first_name')"
          validation-rules="required|max:50|alpha_common_puntuation_marks"
          :name="$t('onboarding_first_name')"
        >
          <OnboardingInput id="member-first-name" v-model="firstName" />
        </OnboardingField>
        <OnboardingField
          for-id="member-last-name"
          :label="$t('onboarding_last_name')"
          validation-rules="required|max:50|alpha_common_puntuation_marks"
          :name="$t('onboarding_last_name')"
        >
          <OnboardingInput id="member-last-name" v-model="lastName" />
        </OnboardingField>
        <OnboardingField
          for-id="member-relationship"
          :label="$t('onboarding_relationship')"
          validation-rules="required"
          :name="$t('onboarding_relationship')"
        >
          <Select id="member-relationship" v-model="relationshipType">
            <option v-for="type in relationshipTypes" :key="type" :value="type">
              {{ $t(`relationship_type_${type}`) }}
            </option>
          </Select>
        </OnboardingField>
        <OnboardingField
          for-id="member-dob"
          :label="$t('onboarding_dob')"
          :name="$t('onboarding_dob')"
          validation-rules="required|validate_date"
        >
          <OnboardingInput
            id="member-dob"
            v-model="dateOfBirth"
            placeholder="    /    /  "
            mask="##/##/####"
            masked
          />
        </OnboardingField>
      </div>

      <template #bottom>
        <div>
          <div class="tw-flex tw-justify-center tw-my-4">
            <Button data-testid="add-button" @click="addMember"
              ><strong class="tw-text-white">+</strong>
              {{ $t('onboarding_add') }}</Button
            >
          </div>
          <BottomActions
            v-if="showBackButton"
            @left-action-click="$emit('prev')"
            @right-action-click="$emit('next')"
          />
          <BottomActions v-else @right-action-click="$emit('next')" />
        </div>
      </template>
    </OnboardingModalContent>
    <OnboardingModalContent v-else>
      <div class="tw-flex tw-justify-center tw-flex-col tw-h-full">
        <p
          class="tw-text-sm xl:tw-text-lg tw-text-center tw-mb-5 tw-font-medium"
        >
          {{ $t('onboarding_household_success_message') }}
        </p>
        <img
          class="tw-mx-auto tw-h-[120px] tw-mb-10"
          src="~/assets/icons/check_circle.svg"
          alt="Success icon"
        />
      </div>
      <template #bottom>
        <div>
          <p class="tw-mb-8 tw-text-center">
            {{ $t('onboarding_household_success_question') }}
          </p>
          <BottomActions
            :left-action-label="$t('onboarding_add_another_member')"
            :right-action-label="$t('onboarding_no_continue')"
            @left-action-click="resetFields"
            @right-action-click="$emit('next')"
          />
        </div>
      </template>
    </OnboardingModalContent>
  </ValidationObserver>
</template>

<script>
import fp from 'lodash/fp'
import { gql } from 'graphql-tag'
import { mapState } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import OnboardingModalContent from './OnboardingModalContent'
import ValidationMixin from '@/components/onboarding/ValidationMixin'
import OnboardingField from '@/components/onboarding/OnboardingField'
import BottomActions from '@/components/onboarding/BottomActions'
import Select from '@/components/onboarding/Select'
import OnboardingInput from '@/components/onboarding/OnboardingInput'
import Button from '@/components/onboarding/Button'
import { RELATIONSHIP_TYPES } from '@/components/onboarding/constants'
import OnboardingMixin from '@/components/onboarding/OnboardingMixin'
import { formatDob } from '@/utils/individual'

export default {
  components: {
    OnboardingField,
    Select,
    OnboardingInput,
    Button,
    BottomActions,
    OnboardingModalContent,
    ValidationObserver,
  },
  mixins: [ValidationMixin, OnboardingMixin],
  data() {
    return {
      success: false,
      family: null,
      address: {},
      firstName: '',
      lastName: '',
      relationshipType: '',
      dateOfBirth: '',
      formError: '',
    }
  },
  apollo: {
    family: {
      query: gql`
        query getFamily {
          me {
            entityUuid
            relationships {
              memberOf(entityType: "family") {
                id
                entityUuid
                meta
                entityType
                relationshipType
              }
            }
          }
        }
      `,
      update: fp.pipe(fp.prop('me.relationships.memberOf'), fp.first),
    },
    address: {
      query: gql`
        query primaryAddress {
          me {
            primaryAddress {
              address1
              city
              state
              zip
            }
          }
        }
      `,
      update: fp.prop('me.primaryAddress'),
    },
  },
  computed: {
    ...mapState('fiiAuth', ['identity']),
    relationshipTypes() {
      return RELATIONSHIP_TYPES
    },
  },
  mounted() {},
  methods: {
    resetFields() {
      this.success = false
      this.firstName = ''
      this.lastName = ''
      this.relationshipType = ''
      this.dateOfBirth = ''
    },
    createFamily() {
      const createFamilyArgs = {
        name: 'Family of ' + this.identity.meta.firstName,
        relationshipTargetEntityUuid: this.identity.entityUuid,
        relationshipType: 'has_member',
      }
      if (this.address) {
        createFamilyArgs.address = this.address.address1
        createFamilyArgs.city = this.address.city
        createFamilyArgs.state = this.address.state
        createFamilyArgs.zipCode = this.address.zip
      }
      return this.$apollo.mutate({
        mutation: gql`
          mutation createFamily($createFamilyArgs: CreateFamilyInput!) {
            me {
              entity {
                createFamily(entityArgs: $createFamilyArgs) {
                  id
                  entityUuid
                }
              }
            }
          }
        `,
        variables: {
          createFamilyArgs,
        },
        update: (store, { data }) => {
          console.log({ data })
        },
      })
    },
    async createFamilyIndividual() {
      const dob = formatDob(new Date(this.dateOfBirth))
      const createIndividualArgs = {
        firstName: this.firstName,
        lastName: this.lastName,
        dob,
        meta: JSON.stringify({
          first_name: this.firstName,
          last_name: this.lastName,
          dob,
        }),
        relationshipTargetEntityUuid: this.family.entityUuid,
        relationshipType: 'member_of',
      }
      const individual = await this.$apollo
        .mutate({
          mutation: gql`
            mutation createIndividual(
              $createIndividualArgs: CreateIndividualInput!
            ) {
              me {
                entity {
                  createIndividual(entityArgs: $createIndividualArgs) {
                    id
                    entityUuid
                    name
                    meta
                  }
                }
              }
            }
          `,
          variables: { createIndividualArgs },
        })
        .then(fp.prop('data.me.entity.createIndividual'))

      return individual
    },
    async createRelationship(individual) {
      const createRelationshipArgs = {
        sourceEntityUuid: this.identity.entityUuid,
        targetEntityUuid: individual.entityUuid,
        relationshipType: this.relationshipType,
      }
      const response = await this.$apollo
        .mutate({
          mutation: gql`
            mutation createRelationship(
              $createRelationshipArgs: CreateRelationshipInput!
            ) {
              me {
                entity {
                  createRelationship(entityArgs: $createRelationshipArgs) {
                    ok
                    message
                  }
                }
              }
            }
          `,
          variables: { createRelationshipArgs },
        })
        .then(fp.prop('data.me.entity.createRelationship'))
      return response.ok
    },
    async addMember() {
      const formValid = await this.validateForm()
      if (!formValid) {
        return
      }

      this.$emit('saving')
      if (!this.family) {
        this.family = await this.createFamily()
      }

      const individual = await this.createFamilyIndividual()
      const ok = await this.createRelationship(individual)
      this.$emit('stoppedSaving')
      if (ok) {
        this.formError = ''
        this.success = true
      } else {
        alert('The request could not be completed.')
      }
    },
  },
}
</script>

<style scoped></style>
